import * as React from 'react';

import type {NotifyMessage} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {Dialog} from 'shells/dialog/dialog';

import {AddToAudiencePopover} from '../../core/audiences/add-to-audience-popover';
import {Notifies} from '../../entity-list/footer/notifies';

import styles from './list-footer.module.css';

type Props = {
    audienceName?: string;
    isWebFxAudience?: boolean;
    selectedItemCount: number;
    onRemove?: () => void;
    onEmailClick?: () => void;
    onExport: () => void;
    onEdit: () => void;
    getRef: (arg1?: HTMLElement | null | undefined) => void;
    onAddToAudience: (audienceId: string, onlyPrimaryContact: boolean) => Promise<any>;
    onAcknowledgeExportDownload: () => void;
    validNotifies: NotifyMessage[];
    hideExport?: boolean;
};

export function PeopleListFooter(props: Props) {
    const removeButtonRef = React.useRef();
    const [showRemoveConfirmation, setShowRemoveConfirmation] = React.useState<boolean>(false);
    const addToAudienceButtonRef = React.useRef();
    const [showAudiencePopover, setShowAudiencePopover] = React.useState<boolean>(false);

    const disabled = props.selectedItemCount === 0;

    return (
        <div ref={props.getRef} className={styles.container}>
            {props.onRemove ? (
                <Button
                    disabled={props.isWebFxAudience || disabled}
                    size='big'
                    variant='danger'
                    onClick={() => {
                        setShowRemoveConfirmation(true);
                    }}
                    getButtonRef={removeButtonRef}
                    tooltipText={
                        props.isWebFxAudience
                            ? 'MCFX audiences cannot be managed in Nutshell'
                            : undefined
                    }
                >
                    Remove from audience
                </Button>
            ) : undefined}
            {props.onEmailClick ? (
                <Button
                    disabled={disabled}
                    variant='secondary'
                    size='big'
                    onClick={props.onEmailClick}
                >
                    Email
                </Button>
            ) : undefined}
            <Button
                disabled={disabled}
                size='big'
                onClick={() => {
                    setShowAudiencePopover(true);
                }}
                getButtonRef={addToAudienceButtonRef}
            >
                Add to{props.audienceName ? ' another ' : ' '}audience
            </Button>
            <Button disabled={disabled} size='big' onClick={props.onEdit}>
                Edit
            </Button>
            {!props.hideExport && (
                <Button size='big' onClick={props.onExport}>
                    Export
                </Button>
            )}
            {props.selectedItemCount > 0 ? (
                <span className={styles['selected-count']}>{props.selectedItemCount} selected</span>
            ) : undefined}
            {props.validNotifies.length > 0 && !props.hideExport ? (
                <Notifies
                    notifies={props.validNotifies}
                    onAcknowledgeExportDownload={props.onAcknowledgeExportDownload}
                />
            ) : undefined}
            {showRemoveConfirmation ? (
                <Dialog
                    anchor={removeButtonRef.current}
                    location='top'
                    isWarning={false}
                    question={`Are you sure you want to remove ${props.selectedItemCount} ${
                        props.selectedItemCount === 1 ? 'person' : 'people'
                    } from ${props.audienceName ? props.audienceName : 'this audience'}?`}
                    onPrimary={() => {
                        setShowRemoveConfirmation(false);

                        if (props.onRemove) {
                            props.onRemove();
                        }
                    }}
                    onSecondary={() => {
                        setShowRemoveConfirmation(false);
                    }}
                />
            ) : undefined}
            {showAudiencePopover ? (
                <AddToAudiencePopover
                    entityType={'contacts'}
                    anchor={addToAudienceButtonRef.current}
                    onCancel={() => setShowAudiencePopover(false)}
                    onSubmit={(audience, onlyPrimaryContact) => {
                        return props
                            .onAddToAudience(audience.value, onlyPrimaryContact)
                            .then(() => {
                                setShowAudiencePopover(false);
                            });
                    }}
                    selectedCount={props.selectedItemCount}
                />
            ) : undefined}
        </div>
    );
}
